'use client';

import type { User } from 'oidc-client-ts';
import type React from 'react';
import { useEffect, useState } from 'react';
import { getUserManager } from '../../lib/oidcClient';
import { AuthContext } from './auth-context';

const isInvalidGrantError = (error: unknown): boolean =>
    error instanceof Error && error.message.toLowerCase().includes('invalid_grant');

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [userManager, setUserManager] = useState<ReturnType<typeof getUserManager> | null>(null);

    useEffect(() => {
        const manager = getUserManager();
        setUserManager(manager);

        const onSilentRenewError = async (error: unknown) => {
            if (isInvalidGrantError(error)) {
                await manager.removeUser();
                setUser(null);
            }
        };

        manager.events.addSilentRenewError(onSilentRenewError);

        manager
            .getUser()
            .then((user) => {
                setUser(user);
                if (user) {
                    void manager
                        .signinSilent()
                        .then((refreshedUser) => {
                            setUser(refreshedUser);
                        })
                        .catch(async (error) => {
                            if (isInvalidGrantError(error)) {
                                await manager.removeUser();
                                setUser(null);
                            }
                        })
                        .finally(() => {
                            setLoading(false);
                        });
                } else {
                    setLoading(false);
                }
            })
            .catch((_error) => {
                setLoading(false);
            });

        return () => {
            manager.events.removeSilentRenewError(onSilentRenewError);
        };
    }, []);

    if (!userManager) return null;

    const signin = () => {
        void userManager.signinRedirect().catch(() => {});
    };
    const signout = () =>
        void userManager
            .signoutRedirect({
                extraQueryParams: {
                    client_id: userManager.settings.client_id,
                    logout_uri: process.env.NEXT_PUBLIC_OIDC_POST_LOGOUT_URI as string,
                },
            })
            .catch(() => {});

    return <AuthContext.Provider value={{ user, loading, signin, signout }}>{children}</AuthContext.Provider>;
};
